// ============================================================================
// client/objet-papier.ts — OR, OBJETS, POISSONS, VER ET GRANDS DÉCORS
// ----------------------------------------------------------------------------
// Lot 6, le dernier du jeu proprement dit. Trois familles qui n'ont en commun
// que d'être restées en pixel après les cinq premiers lots :
//
//   · CE QUI SE RAMASSE (or, bombe, marteau, tazer, clé, corde) : ombre portée
//     franche, teinte vive. Ce sont les objets les plus importants de l'écran
//     après le héros, ils ont le droit d'être les plus voyants.
//   · LES ENNEMIS AQUATIQUES et le ver de reprise : silhouette avant tout.
//   · LES SIX GRANDS DÉCORS (arbre, épave, baleine, grue, vitrail, campement) :
//     décor, donc AUCUNE ombre portée, comme les 31 petits.
// ============================================================================

import { bruit, decoupe, frange, papier, rect, rgba } from './papier'

type Ctx = CanvasRenderingContext2D
const OMBRE_TON = '#2e2214'
const ENCRE = '#3a2c1e'
const PAP = '#f2e4cc'

export function objetPapier(g: Ctx, x: number, y: number, S: number, kind: string): void {
  const c = { x: x + S / 2, y: y + S / 2 }
  const boite = { x: x - S, y: y - S, w: S * 3, h: S * 3 }
  if (kind === 'or') { decoupe(g, () => { g.beginPath(); g.arc(c.x, c.y, 17, 0, 7) }, '#ffc61f', 'guilloche', boite)
    g.fillStyle = rgba('#fff8d8', .9); g.beginPath(); g.arc(c.x - 5, c.y - 5, 5, 0, 7); g.fill() }
  else if (kind === 'bomb') { decoupe(g, () => { g.beginPath(); g.arc(c.x, c.y + 4, 15, 0, 7) }, '#2e2438', 'grain', boite)
    g.strokeStyle = '#c07a34'; g.lineWidth = 3; g.lineCap = 'round'
    g.beginPath(); g.moveTo(c.x + 7, c.y - 8); g.quadraticCurveTo(c.x + 16, c.y - 18, c.x + 8, c.y - 24); g.stroke()
    g.fillStyle = '#ff9a2e'; g.beginPath(); g.arc(c.x + 8, c.y - 26, 4, 0, 7); g.fill() }
  else if (kind === 'jackhammer') { decoupe(g, () => { g.beginPath(); g.rect(c.x - 8, c.y - 18, 16, 22) }, '#c8b03a', 'quadrille', boite)
    decoupe(g, () => { g.beginPath(); g.moveTo(c.x - 5, c.y + 4); g.lineTo(c.x + 5, c.y + 4); g.lineTo(c.x + 2, c.y + 20); g.lineTo(c.x - 2, c.y + 20); g.closePath() }, '#9aa2ac', 'plis', boite) }
  else if (kind === 'appat') { // APPÂT (31/07) : une cuisse de poulet. Le seul objet qui se
    // lit sans notice — un moine court dessus, c'est évident. Os clair + viande dorée.
    decoupe(g, () => { g.beginPath(); g.ellipse(c.x + 2, c.y + 4, 13, 10, -0.5, 0, 7) }, '#c8742f', 'grain', boite)
    decoupe(g, () => { g.beginPath(); g.moveTo(c.x - 4, c.y - 2); g.lineTo(c.x - 14, c.y - 14); g.lineTo(c.x - 8, c.y - 18); g.lineTo(c.x + 1, c.y - 7); g.closePath() }, '#f2e4cc', 'plis', boite)
    g.fillStyle = rgba('#fff8d8', .95); g.beginPath(); g.arc(c.x - 13, c.y - 16, 5, 0, 7); g.fill()
    g.fillStyle = rgba('#ffb13d', .8); g.beginPath(); g.arc(c.x + 5, c.y + 1, 4, 0, 7); g.fill() }
  else if (kind === 'bandelette') { // BANDELETTE (05/08) : un rouleau de bandes de papier, un pan
    // qui pend déroulé. Les deux points sombres du bout rappellent les yeux de la momie à venir.
    decoupe(g, () => { g.beginPath(); g.arc(c.x - 2, c.y - 3, 13, 0, 7) }, '#e8ddc4', 'plis', boite)
    g.strokeStyle = rgba('#b9a887', .85); g.lineWidth = 2
    for (let i = -1; i <= 1; i++) { g.beginPath(); g.arc(c.x - 2, c.y - 3, 5 + i * 4, .6, 3.4); g.stroke() }
    decoupe(g, () => { g.beginPath(); g.moveTo(c.x + 8, c.y + 2); g.lineTo(c.x + 15, c.y + 16)
      g.lineTo(c.x + 8, c.y + 20); g.lineTo(c.x + 3, c.y + 6); g.closePath() }, '#f2e4cc', 'plis', boite)
    g.fillStyle = rgba(ENCRE, .55)
    g.beginPath(); g.arc(c.x - 5, c.y - 5, 1.8, 0, 7); g.fill()
    g.beginPath(); g.arc(c.x + 1, c.y - 6, 1.8, 0, 7); g.fill() }
  else if (kind === 'meute') { // MEUTE PRIVÉE (05/08) : une cloche d'appel noire à liseré rouge —
    // on l'agite et deux spectres répondent. La forme dit « ça convoque quelque chose ».
    decoupe(g, () => { g.beginPath(); g.moveTo(c.x - 13, c.y + 10); g.quadraticCurveTo(c.x - 12, c.y - 12, c.x, c.y - 15)
      g.quadraticCurveTo(c.x + 12, c.y - 12, c.x + 13, c.y + 10); g.closePath() }, '#3b2f4a', 'grain', boite)
    g.fillStyle = rgba('#c0392b', .9); g.fillRect(c.x - 14, c.y + 8, 28, 4)                       // le liseré rouge
    g.fillStyle = '#a06a34'; g.fillRect(c.x - 2, c.y - 22, 4, 8)                                  // la poignée
    g.fillStyle = rgba('#f2e4cc', .5)                                                             // le battant
    g.beginPath(); g.arc(c.x, c.y + 13, 3.4, 0, 7); g.fill()
    g.fillStyle = rgba('#ff5f57', .7)                                                             // deux yeux qui attendent dedans
    g.beginPath(); g.arc(c.x - 4, c.y - 1, 1.8, 0, 7); g.fill()
    g.beginPath(); g.arc(c.x + 4, c.y - 1, 1.8, 0, 7); g.fill() }
  else if (kind === 'fantome') { // FANTÔME DE PAPIER (05/08) : un drap translucide à deux yeux,
    // bas déchiqueté. Le seul objet du jeu qu'on voit à travers : il annonce ce qu'il fait.
    g.globalAlpha = .72
    decoupe(g, () => { g.beginPath(); g.moveTo(c.x - 13, c.y + 14); g.lineTo(c.x - 13, c.y - 4)
      g.quadraticCurveTo(c.x - 13, c.y - 18, c.x, c.y - 18); g.quadraticCurveTo(c.x + 13, c.y - 18, c.x + 13, c.y - 4)
      g.lineTo(c.x + 13, c.y + 14); g.lineTo(c.x + 7, c.y + 8); g.lineTo(c.x, c.y + 14)
      g.lineTo(c.x - 7, c.y + 8); g.closePath() }, '#dfe7f2', 'plis', boite)
    g.globalAlpha = 1
    g.fillStyle = rgba(ENCRE, .7)
    g.beginPath(); g.ellipse(c.x - 5, c.y - 6, 2.6, 3.4, 0, 0, 7); g.fill()
    g.beginPath(); g.ellipse(c.x + 5, c.y - 6, 2.6, 3.4, 0, 0, 7); g.fill() }
  else if (kind === 'couronne') { // COURONNE MAUDITE (05/08) : l'or qui appelle les moines.
    // Trois pointes à perles, un bandeau serti — et le violet des moines dans les pierres :
    // c'est le seul trésor du jeu qui porte la couleur de ceux qui vont venir te chercher.
    decoupe(g, () => { g.beginPath()
      g.moveTo(c.x - 15, c.y + 10); g.lineTo(c.x - 15, c.y - 4); g.lineTo(c.x - 8, c.y + 2)
      g.lineTo(c.x, c.y - 12); g.lineTo(c.x + 8, c.y + 2); g.lineTo(c.x + 15, c.y - 4)
      g.lineTo(c.x + 15, c.y + 10); g.closePath() }, '#ffc61f', 'guilloche', boite)
    g.fillStyle = rgba('#8b4fd8', .95)                                                            // les pierres, violet de moine
    for (const p of [[-15, -6], [0, -14], [15, -6]] as [number, number][]) { g.beginPath(); g.arc(c.x + p[0], c.y + p[1], 3.2, 0, 7); g.fill() }
    g.fillStyle = rgba('#fff8d8', .85); g.fillRect(c.x - 15, c.y + 5, 30, 3) }                    // le bandeau
  else if (kind === 'patate') { // PATATE CHAUDE (05/08) : une bombe RONDE en papier, mèche
    // torsadée. Éteinte elle est grise et sage ; le jeu la peint en rouge et la fait battre
    // quand elle est allumée (drawPatate) — le dessin d'objet reste sa version au repos.
    decoupe(g, () => { g.beginPath(); g.ellipse(c.x, c.y + 4, 15, 13, .12, 0, 7) }, '#c8863a', 'grain', boite)
    g.fillStyle = rgba('#8a5a24', .55)
    g.beginPath(); g.ellipse(c.x - 5, c.y + 2, 3.4, 2.4, .5, 0, 7); g.fill()
    g.beginPath(); g.ellipse(c.x + 6, c.y + 8, 2.8, 2, -.3, 0, 7); g.fill()
    g.strokeStyle = '#7a6a52'; g.lineWidth = 3; g.lineCap = 'round'
    g.beginPath(); g.moveTo(c.x + 4, c.y - 8); g.quadraticCurveTo(c.x + 13, c.y - 17, c.x + 6, c.y - 23); g.stroke() }
  else if (kind === 'maillet') { // MAILLET À RESSORT (05/08, l'idée de Claude-Alain) : la tête de
    // bois à plat, le RESSORT en dessous (c'est lui qui expulse), le manche kraft. À 44 px, le
    // ressort se lit par trois spires épaisses — c'est la silhouette qui dit l'objet.
    g.fillStyle = '#a06a34'; g.fillRect(c.x - 2, c.y - 2, 5, 22)                                  // le manche
    g.strokeStyle = '#9aa8b4'; g.lineWidth = 3.5; g.lineCap = 'round'
    for (let i = 0; i < 3; i++) {                                                                 // les spires
      g.beginPath(); g.ellipse(c.x, c.y - 5 - i * 4, 8 - i, 2.6, 0, 0, 7); g.stroke()
    }
    decoupe(g, () => { g.beginPath(); g.rect(c.x - 14, c.y - 24, 28, 11) }, '#c8863a', 'grain', boite) // la tête
    g.fillStyle = rgba('#8a5a24', .9); g.fillRect(c.x - 14, c.y - 24, 4, 11); g.fillRect(c.x + 10, c.y - 24, 4, 11) }
  else if (kind === 'deguisement') { // DÉGUISEMENT DE MOINE (05/08) : la capuche violette du moine
    // normal, VIDE (deux trous d'yeux clairs), tenue à la main par un petit bâton — un masque,
    // pas un moine : c'est le vide des yeux qui fait la différence à 44 px.
    g.fillStyle = '#8a6038'; g.fillRect(c.x - 1, c.y + 6, 3, 14)                                    // le bâton
    decoupe(g, () => { g.beginPath(); g.moveTo(c.x - 12, c.y + 8); g.quadraticCurveTo(c.x - 14, c.y - 16, c.x, c.y - 18)
      g.quadraticCurveTo(c.x + 14, c.y - 16, c.x + 12, c.y + 8); g.closePath() }, '#8b4fd8', 'plis', boite)
    g.fillStyle = rgba('#f6efe0', .92)
    g.beginPath(); g.ellipse(c.x - 5, c.y - 4, 3, 3.6, 0, 0, 7); g.fill()                            // les yeux VIDES
    g.beginPath(); g.ellipse(c.x + 5, c.y - 4, 3, 3.6, 0, 0, 7); g.fill() }
  else if (kind === 'potion') { // POTION DE GÉANT (05/08) : fiole ronde au breuvage champignon —
    // le rouge à pois blancs dit « grandir » sans notice, le liège dit « à boire ».
    decoupe(g, () => { g.beginPath(); g.arc(c.x, c.y + 4, 14, 0, 7) }, '#b93a34', 'grain', boite)
    g.fillStyle = rgba('#f6efe0', .95)
    g.beginPath(); g.arc(c.x - 5, c.y + 1, 3, 0, 7); g.fill()
    g.beginPath(); g.arc(c.x + 5, c.y + 6, 2.4, 0, 7); g.fill()
    g.beginPath(); g.arc(c.x - 1, c.y + 10, 1.9, 0, 7); g.fill()
    decoupe(g, () => { g.beginPath(); g.rect(c.x - 4, c.y - 15, 8, 8) }, '#cfd8de', 'quadrille', boite) // le col de verre
    g.fillStyle = '#a06a34'; g.fillRect(c.x - 5, c.y - 20, 10, 6)                                       // le bouchon de liège
    g.fillStyle = rgba('#ffffff', .5); g.beginPath(); g.arc(c.x - 6, c.y - 1, 2.2, 0, 7); g.fill() }    // le reflet du verre
  else if (kind === 'tazer') { decoupe(g, () => { g.beginPath(); g.rect(c.x - 7, c.y - 6, 14, 20) }, '#3f5a8a', 'quadrille', boite)
    g.fillStyle = '#6afcff'; g.beginPath()
    g.moveTo(c.x - 2, c.y - 22); g.lineTo(c.x + 7, c.y - 10); g.lineTo(c.x + 1, c.y - 10)
    g.lineTo(c.x + 6, c.y + 1); g.lineTo(c.x - 6, c.y - 12); g.lineTo(c.x, c.y - 12); g.closePath(); g.fill() }
  else if (kind === 'key') { decoupe(g, () => { g.beginPath(); g.arc(c.x - 6, c.y - 6, 9, 0, 7) }, '#ffc61f', 'guilloche', boite)
    g.fillStyle = PAP; g.beginPath(); g.arc(c.x - 6, c.y - 6, 4, 0, 7); g.fill()
    g.fillStyle = '#ffc61f'; g.fillRect(c.x - 1, c.y - 3, 5, 20); g.fillRect(c.x + 3, c.y + 8, 7, 4); g.fillRect(c.x + 3, c.y + 15, 7, 4) }
  else if (kind === 'rope') { g.strokeStyle = rgba(OMBRE_TON, .28); g.lineWidth = 5
    g.beginPath(); g.arc(c.x + 3, c.y + 4, 13, 0, 7); g.stroke()
    g.strokeStyle = '#a06a34'; g.lineWidth = 5; g.beginPath(); g.arc(c.x, c.y, 13, 0, 7); g.stroke()
    g.strokeStyle = '#cf9048'; g.lineWidth = 2; g.beginPath(); g.arc(c.x, c.y, 13, .4, 2.6); g.stroke() }
  /* ⚠️ LE MÉTAL SE TESTE AVANT LE BOIS. Le fourre-tout `kind.startsWith('caisse-')`
     attrapait aussi « caisse-metal », qui n'atteignait donc JAMAIS sa branche :
     toutes les caisses du jeu sont sorties en bois pendant une journée. Le pire
     n'était pas l'apparence — une caisse de métal ne se casse jamais en tombant,
     c'est sa nature, mais déguisée en bois elle passait pour une caisse cassable
     qui refuse de se casser. Une faute d'ordre de conditions a donc eu l'air d'un
     bug de physique. Ne pas remettre de condition attrape-tout en premier. */
  else if (kind === 'caisse-metal') { rect(g, x + 5, y + 8, S - 10, S - 12, '#8f9aa8', 'quadrille'); frange(g, x + 5, y + 8, S - 10, '#b4bec8')
    g.fillStyle = rgba(ENCRE, .4); for (const r of [[10, 14], [S - 14, 14], [10, S - 12], [S - 14, S - 12]] as [number, number][]) { g.beginPath(); g.arc(x + r[0], y + r[1], 2.6, 0, 7); g.fill() }
    /* deux cerclages d'acier en croix : à 44 px la couleur seule ne suffit pas à
       dire « celle-ci ne se cassera pas », il faut que la FORME le dise aussi. */
    g.strokeStyle = rgba('#e8eef4', .5); g.lineWidth = 3
    g.beginPath(); g.moveTo(x + 5, y + S / 2 + 1); g.lineTo(x + S - 5, y + S / 2 + 1)
    g.moveTo(x + S / 2, y + 8); g.lineTo(x + S / 2, y + S - 4); g.stroke() }
  else if (kind.startsWith('caisse-')) {
    rect(g, x + 5, y + 8, S - 10, S - 12, '#a06a34', 'quadrille'); frange(g, x + 5, y + 8, S - 10, '#cf9048')
    g.strokeStyle = rgba(ENCRE, .34); g.lineWidth = 2
    g.beginPath(); g.moveTo(x + 7, y + 10); g.lineTo(x + S - 7, y + S - 6); g.moveTo(x + S - 7, y + 10); g.lineTo(x + 7, y + S - 6); g.stroke()
    const q = kind.slice(7)          // « caisse- » fait SEPT caractères, pas huit
    if (q === 'or') { g.fillStyle = '#ffc61f'; g.beginPath(); g.arc(c.x, c.y + 3, 7, 0, 7); g.fill() }
    else if (q === 'bombe') { g.fillStyle = '#2e2438'; g.beginPath(); g.arc(c.x, c.y + 4, 7, 0, 7); g.fill() }
    else if (q === 'cle') { g.fillStyle = '#ffc61f'; g.beginPath(); g.arc(c.x - 3, c.y, 5, 0, 7); g.fill(); g.fillRect(c.x, c.y + 1, 3, 11) }
  }
  else if (kind === 'rocher') { decoupe(g, () => { g.beginPath(); g.arc(c.x, c.y + 3, 19, 0, 7) }, '#8a8272', 'marbre', boite)
    g.fillStyle = rgba('#ffffff', .25); g.beginPath(); g.arc(c.x - 6, c.y - 4, 6, 0, 7); g.fill() }
}

/* ═══════════════════════════════════ les ennemis aquatiques et le ver ════ */
/** piranha · meduse · ver. Comme les compagnons : la SILHOUETTE fait la
 *  reconnaissance, à la taille où on les voit vraiment. */
export function fishPapier(g: Ctx, x: number, y: number, S: number, kind: string): void {
  const c = { x: x + S / 2, y: y + S / 2 }
  const boite = { x: x - S, y: y - S, w: S * 3, h: S * 3 }
  const D = (path: () => void, col: string, motif: string): void =>
    decoupe(g, () => { g.beginPath(); path() }, col, motif, boite, S * .05, S * .06)
  const u = S / 48
  if (kind === 'piranha') {
    D(() => g.ellipse(c.x - 2 * u, c.y, 19 * u, 11 * u, 0, 0, 7), '#5a8ac0', 'chevrons')
    D(() => { g.moveTo(c.x + 14 * u, c.y); g.lineTo(c.x + 26 * u, c.y - 9 * u)
      g.lineTo(c.x + 26 * u, c.y + 9 * u); g.closePath() }, '#3f6a9a', 'plis')
    g.fillStyle = '#ffffff'; g.beginPath(); g.moveTo(c.x - 21 * u, c.y)
    for (let k = 0; k < 4; k++) { g.lineTo(c.x + (-18 + k * 4.5) * u, c.y - 6 * u); g.lineTo(c.x + (-15.5 + k * 4.5) * u, c.y + 2 * u) }
    g.fill()
    g.fillStyle = ENCRE; g.beginPath(); g.arc(c.x - 8 * u, c.y - 4 * u, 2.2 * u, 0, 7); g.fill()
  } else if (kind === 'meduse') {
    D(() => g.ellipse(c.x, c.y - 4 * u, 18 * u, 14 * u, 0, Math.PI, 0), '#e88ac0', 'pois')
    g.strokeStyle = rgba('#e88ac0', .8); g.lineWidth = 2.6 * u; g.lineCap = 'round'
    for (let k = 0; k < 5; k++) { g.beginPath(); g.moveTo(c.x + (-14 + k * 7) * u, c.y - 4 * u)
      g.quadraticCurveTo(c.x + (-11 + k * 7) * u, c.y + 9 * u, c.x + (-15 + k * 7) * u, c.y + 21 * u); g.stroke() }
  } else {                                   // le ver de reprise
    D(() => { g.ellipse(c.x - 16 * u, c.y + 6 * u, 9 * u, 8 * u, 0, 0, 7)
      g.ellipse(c.x - 1 * u, c.y + 2 * u, 10 * u, 9 * u, 0, 0, 7)
      g.ellipse(c.x + 15 * u, c.y + 6 * u, 9 * u, 8 * u, 0, 0, 7) }, '#e08a70', 'pois')
    g.fillStyle = ENCRE; g.beginPath(); g.arc(c.x + 18 * u, c.y + 3 * u, 1.9 * u, 0, 7); g.fill()
  }
}

/* ══════════════════════════════════════════ les six grands décors ════════ */
/** L'emprise est donnée par le jeu (w×h cases). AUCUNE ombre portée : c'est du
 *  décor, il reste collé au fond comme les 31 petits. */
export function grandDecoPapier(g: Ctx, x: number, y: number, W: number, H: number, kind: string): void {
  const boite = { x: x - W, y: y - H, w: W * 3, h: H * 3 }
  const D = (path: () => void, col: string, motif: string): void =>
    decoupe(g, () => { g.beginPath(); path() }, col, motif, boite, 0, 0, 0)
  const cx = x + W / 2, bas = y + H
  if (kind === 'arbre') {
    D(() => g.rect(cx - W * .1, bas - H * .58, W * .2, H * .58), '#8a6033', 'quadrille')
    D(() => { g.arc(cx - W * .2, bas - H * .66, W * .26, 0, 7)
      g.arc(cx + W * .2, bas - H * .66, W * .26, 0, 7); g.arc(cx, bas - H * .86, W * .3, 0, 7) }, '#4a8a3a', 'pois')
  } else if (kind === 'epave') {
    D(() => { g.moveTo(x + W * .04, bas - H * .18); g.quadraticCurveTo(cx, bas - H * .9, x + W * .96, bas - H * .18)
      g.quadraticCurveTo(cx, bas - H * .04, x + W * .04, bas - H * .18) }, '#8a6033', 'plis')
    g.strokeStyle = rgba(ENCRE, .3); g.lineWidth = Math.max(1, W / 90)
    for (let k = 0; k < 4; k++) { const px = x + W * (.2 + k * .2)
      g.beginPath(); g.moveTo(px, bas - H * .5); g.lineTo(px, bas - H * .16); g.stroke() }
  } else if (kind === 'baleine') {
    g.strokeStyle = rgba('#f0ead6', .72); g.lineWidth = Math.max(2, W / 60); g.lineCap = 'round'
    g.beginPath(); g.moveTo(x + W * .06, bas - H * .2)
    g.quadraticCurveTo(cx, bas - H * .95, x + W * .94, bas - H * .3); g.stroke()
    g.lineWidth = Math.max(1.4, W / 90)
    for (let k = 0; k < 10; k++) { const t = k / 10, px = x + W * (.1 + t * .8)
      const py = bas - H * .2 - Math.sin(t * Math.PI) * H * .5
      g.beginPath(); g.moveTo(px, py); g.lineTo(px + W * .01, py + H * .3); g.stroke() }
    D(() => g.ellipse(x + W * .08, bas - H * .24, W * .07, H * .18, -.3, 0, 7), '#f0ead6', 'grain')
  } else if (kind === 'grue') {
    D(() => g.rect(x + W * .1, bas - H, W * .16, H), '#c07a34', 'quadrille')
    D(() => g.rect(x + W * .1, bas - H, W * .84, H * .14), '#c07a34', 'quadrille')
    g.strokeStyle = '#8f9aa8'; g.lineWidth = Math.max(1.6, W / 70)
    g.beginPath(); g.moveTo(x + W * .8, bas - H * .86); g.lineTo(x + W * .8, bas - H * .45); g.stroke()
    D(() => g.arc(x + W * .8, bas - H * .38, W * .08, .6, 5.2), '#8f9aa8', 'plis')
  } else if (kind === 'vitrail') {
    D(() => { g.moveTo(x, bas); g.lineTo(x, bas - H * .6)
      g.quadraticCurveTo(cx, bas - H * 1.05, x + W, bas - H * .6); g.lineTo(x + W, bas) }, '#5a4a6a', 'grain')
    const cols = ['#c83a5a', '#3a7ac8', '#c8a03a', '#4aa06a']
    for (let k = 0; k < 4; k++) D(() => g.ellipse(x + W * (.3 + (k % 2) * .4), bas - H * (.3 + ((k / 2) | 0) * .28),
      W * .12, H * .16, 0, 0, 7), cols[k] ?? '#c83a5a', 'guilloche')
  } else if (kind === 'campement') {
    D(() => { g.moveTo(x + W * .08, bas); g.lineTo(x + W * .38, bas - H * .78); g.lineTo(x + W * .68, bas); g.closePath() },
      '#c8a878', 'chevrons')
    g.strokeStyle = rgba(ENCRE, .4); g.lineWidth = Math.max(1.2, W / 90)
    g.beginPath(); g.moveTo(x + W * .38, bas - H * .78); g.lineTo(x + W * .38, bas); g.stroke()
    g.fillStyle = '#8a6033'
    for (const dx of [-.04, 0, .04]) g.fillRect(x + W * (.84 + dx), bas - H * .18, W * .025, H * .18)
    D(() => { g.moveTo(x + W * .8, bas - H * .18); g.lineTo(x + W * .86, bas - H * .5)
      g.lineTo(x + W * .92, bas - H * .18); g.closePath() }, '#ff9a2e', 'marbre')
  }
}

/* ----------------------------------------------------------------- le cache */
const CACHE = new Map<string, HTMLCanvasElement>()
const CACHE_MAX = 70

function cache(cle: string, w: number, h: number, peindre: (g: Ctx) => void): HTMLCanvasElement {
  let cv = CACHE.get(cle)
  if (!cv) {
    cv = document.createElement('canvas')
    cv.width = Math.max(1, Math.ceil(w)); cv.height = Math.max(1, Math.ceil(h))
    const g = cv.getContext('2d')
    if (g) peindre(g)
    if (CACHE.size >= CACHE_MAX) { const k = CACHE.keys().next().value; if (k) CACHE.delete(k) }
    CACHE.set(cle, cv)
  }
  return cv
}

const M = 0.4   // marge : la mèche de la bombe et la queue du piranha débordent

/** L'or et les cinq objets ramassables. */
export function blitObjet(ctx: Ctx, kind: string, S: number, x: number, y: number): void {
  const T = Math.round(S), m = Math.ceil(T * M)
  const cv = cache(`o|${kind}|${T}`, T + m * 2, T + m * 2, (g) => objetPapier(g, m, m, T, kind))
  ctx.drawImage(cv, x - m, y - m)
}

/** Les ennemis aquatiques et le ver. */
export function blitFish(ctx: Ctx, kind: string, S: number, x: number, y: number): void {
  const T = Math.round(S), m = Math.ceil(T * M)
  const cv = cache(`f|${kind}|${T}`, T + m * 2, T + m * 2, (g) => fishPapier(g, m, m, T, kind))
  ctx.drawImage(cv, x - m, y - m)
}

/** Un grand décor, sur son emprise réelle en cases. */
export function blitGrandDeco(ctx: Ctx, kind: string, W: number, H: number, x: number, y: number): void {
  const w = Math.round(W), h = Math.round(H)
  const cv = cache(`G|${kind}|${w}x${h}`, w, h, (g) => grandDecoPapier(g, 0, 0, w, h, kind))
  ctx.drawImage(cv, x, y)
}
